

import React, { useState, useCallback } from 'react';
import ReactQuill from 'react-quill';

// Toolbar configuration — all options the client needs
const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'underline', 'italic', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image', 'video'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['clean'],
  ],
};

const formats = [
  'header', 'font', 'size',
  'bold', 'underline', 'italic', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
  'color', 'background', 'align',
];

// Count words in an HTML string
const countWords = (html) => {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.split(' ').length : 0;
};

const countChars = (html) => {
  return html.replace(/<[^>]*>/g, '').length;
};

// ── RichTextEditor ────────────────────────────────────────────────────────────
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter content here...',
  height = 300,
  label,
}) => {
  const [mode, setMode]           = useState('visual'); // 'visual' | 'source'
  const [showPreview, setShowPreview] = useState(false);

  const handleSourceChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  const words = countWords(value || '');
  const chars = countChars(value || '');

  return (
    <div style={{ width: '100%', marginBottom: '1rem' }}>

      {/* ── Toolbar row ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        padding: '6px 10px',
        background: '#f8f9fa',
        border: '1px solid #d1d5db',
        borderBottom: 'none',
        borderRadius: '6px 6px 0 0',
      }}>
        {label && (
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginRight: 8 }}>
            {label}
          </span>
        )}

        {/* Visual / Source toggle */}
        <button
          type="button"
          onClick={() => setMode('visual')}
          style={{
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 4,
            border: '1px solid',
            cursor: 'pointer',
            background: mode === 'visual' ? '#2563eb' : '#fff',
            color: mode === 'visual' ? '#fff' : '#374151',
            borderColor: mode === 'visual' ? '#2563eb' : '#d1d5db',
          }}
        >
          Visual
        </button>

        <button
          type="button"
          onClick={() => setMode('source')}
          style={{
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 4,
            border: '1px solid',
            cursor: 'pointer',
            background: mode === 'source' ? '#2563eb' : '#fff',
            color: mode === 'source' ? '#fff' : '#374151',
            borderColor: mode === 'source' ? '#2563eb' : '#d1d5db',
            fontFamily: 'monospace',
          }}
        >
          {'</> HTML'}
        </button>

        {/* Preview button */}
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          style={{
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 4,
            border: '1px solid #16a34a',
            cursor: 'pointer',
            background: '#fff',
            color: '#16a34a',
          }}
        >
          Preview
        </button>

        {/* Word / char count */}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>
          {words} words · {chars} chars
        </span>
      </div>

      {/* ── Visual editor ────────────────────────────────────────────────── */}
      {mode === 'visual' && (
        <div style={{ border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
          <ReactQuill
            value={value}
            onChange={onChange}
            theme="snow"
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{ minHeight: height }}
          />
        </div>
      )}

      {/* ── HTML source editor ───────────────────────────────────────────── */}
      {mode === 'source' && (
        <div style={{ border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
          <div style={{
            padding: '6px 10px',
            background: '#1f2937',
            borderBottom: '1px solid #374151',
            fontSize: 11,
            color: '#9ca3af',
          }}>
            Editing raw HTML — switch to Visual to see formatted output
          </div>
          <textarea
            value={value}
            onChange={handleSourceChange}
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: height,
              padding: '12px',
              fontFamily: '"Courier New", monospace',
              fontSize: 13,
              lineHeight: 1.6,
              background: '#111827',
              color: '#a3e635',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
            placeholder="<p>Enter HTML here...</p>"
          />
        </div>
      )}

      {/* ── Preview modal ────────────────────────────────────────────────── */}
      {showPreview && (
        <div
          onClick={() => setShowPreview(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 780,
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 20px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb',
            }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
                Content Preview
              </span>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#6b7280',
                  lineHeight: 1,
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>

            {/* Modal content */}
            <div
              style={{
                padding: '24px',
                overflowY: 'auto',
                flex: 1,
                fontSize: 15,
                lineHeight: 1.7,
                color: '#111827',
              }}
              // Safe here — this is admin-only content that the admin themselves wrote
              dangerouslySetInnerHTML={{ __html: value || '<p style="color:#9ca3af">Nothing to preview yet.</p>' }}
            />

            {/* Modal footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e5e7eb',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                style={{
                  padding: '8px 20px',
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;